import {useState} from "react";
import {useTranslation} from "react-i18next";
import {SingleDatePicker} from "react-dates";
import moment, {Moment} from "moment";
import "moment/locale/ar";
import "moment/locale/en-gb";
import {Button, Select, SelectItem} from "@wfp/ui";
import {KVPair} from "../../models/types";
import {useRetrievePartners} from "../../hooks/partner";
import {useRetrieveSchools} from "../../hooks/school";
import {useRetrieveLossCategories} from "../../hooks/loss-category";
import {dateFormat} from "../../hooks/formats";

interface Props {
    onSubmit: (filters: KVPair[]) => void;
}

export const LossFilters = (props: Props) => {
    const {t, i18n} = useTranslation();
    const [partner, setPartner] = useState<number>();
    const [school, setSchool] = useState<number>();
    const [category, setCategory] = useState<number>();
    const [createdOnAfter, setCreatedOnAfter] = useState<Moment>();
    const [createdOnBefore, setCreatedOnBefore] = useState<Moment>();
    const [focusAfter, setFocusAfter] = useState(false);
    const [focusBefore, setFocusBefore] = useState(false);

    const partners = useRetrievePartners({});
    const schools = useRetrieveSchools({});
    const categories = useRetrieveLossCategories({});

    function handleSubmit() {
        const filters: KVPair[] = [];
        if (partner) {
            filters.push({key: 'partner', value: partner.toString()});
        }
        if (school) {
            filters.push({key: 'school', value: school.toString()});
        }
        if (category) {
            filters.push({key: 'category', value: category.toString()});
        }
        if (createdOnAfter) {
            const formattedDate = dateFormat(createdOnAfter);
            if (formattedDate) {
                filters.push({key: 'created_on_after', value: formattedDate});
            }
        }
        if (createdOnBefore) {
            const formattedDate = dateFormat(createdOnBefore);
            if (formattedDate) {
                filters.push({key: 'created_on_before', value: formattedDate});
            }
        }
        props.onSubmit(filters);
    }

    return (
        <div className="mb-4 p-4 bg-gray-50 rounded">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                    <Select
                        labelText={t('partner')}
                        name="partner"
                        value={partner || ''}
                        onChange={(e: any) => setPartner(e.target.value ? Number(e.target.value) : undefined)}
                    >
                        <SelectItem value="" text={t('all')}/>
                        {partners?.map((p) => (
                            <SelectItem key={p.id} value={p.id} text={p.name}/>
                        ))}
                    </Select>
                </div>
                <div>
                    <Select
                        labelText={t('school')}
                        name="school"
                        value={school || ''}
                        onChange={(e: any) => setSchool(e.target.value ? Number(e.target.value) : undefined)}
                    >
                        <SelectItem value="" text={t('all')}/>
                        {schools?.map((s) => (
                            <SelectItem key={s.id} value={s.id} text={s.name}/>
                        ))}
                    </Select>
                </div>
                <div>
                    <Select
                        labelText={t('category')}
                        name="category"
                        value={category || ''}
                        onChange={(e: any) => setCategory(e.target.value ? Number(e.target.value) : undefined)}
                    >
                        <SelectItem value="" text={t('all')}/>
                        {categories?.map((c) => (
                            <SelectItem key={c.id} value={c.id} text={c.name}/>
                        ))}
                    </Select>
                </div>
                <div>
                    <label htmlFor="fromDateFilter" className="wfp--label">
                        {t("from_date")}
                    </label>
                    <SingleDatePicker
                        isRTL={i18n.dir() === 'rtl'}
                        renderMonthText={month => {
                            month.locale(i18n.language)
                            return (moment(month).format('MMMM YYYY'))
                        }}
                        renderDayContents={day => {
                            day.locale('en')
                            return (moment(day).format('D'))
                        }}
                        date={createdOnAfter!}
                        isOutsideRange={() => false}
                        onDateChange={(date) => setCreatedOnAfter(date ?? undefined)}
                        focused={focusAfter}
                        onFocusChange={(f) => setFocusAfter(f.focused)}
                        numberOfMonths={1}
                        showClearDate={true}
                        displayFormat="DD/MM/YYYY"
                        id="fromDateFilter"
                    />
                </div>
                <div>
                    <label htmlFor="toDateFilter" className="wfp--label">
                        {t("to_date")}
                    </label>
                    <SingleDatePicker
                        isRTL={i18n.dir() === 'rtl'}
                        renderMonthText={month => {
                            month.locale(i18n.language)
                            return (moment(month).format('MMMM YYYY'))
                        }}
                        renderDayContents={day => {
                            day.locale('en')
                            return (moment(day).format('D'))
                        }}
                        date={createdOnBefore!}
                        isOutsideRange={() => false}
                        onDateChange={(date) => setCreatedOnBefore(date ?? undefined)}
                        focused={focusBefore}
                        onFocusChange={(f) => setFocusBefore(f.focused)}
                        numberOfMonths={1}
                        showClearDate={true}
                        displayFormat="DD/MM/YYYY"
                        id="toDateFilter"
                    />
                </div>
                <div className="flex items-end">
                    <Button onClick={handleSubmit}>
                        {t('apply')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
